import { Box, Divider } from "@mui/material";
import { LayersFill, Ruler } from "@procore/core-icons";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { EventOrigin, Events, track } from "apis/procore";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import type { ToolbarButtonEntry } from "contexts/toolbars";
import { toolbarsActions, useDispatchToolbars, useToolbarButtons } from "contexts/toolbars";
import { getOrthoFitClipPlane, getOrthoFromPinhole } from "features/orthoCam/utils";
import {
    CameraType,
    renderActions,
    selectCameraType,
    selectIsXRayActive,
    selectMainObject,
    selectMainObjectPick,
} from "features/render";
import { useToggleXRayMode } from "features/render/hooks/useToggleXRayMode";
import { useEnterToolMode } from "features/toolMode/useEnterToolMode";
import OrthographicIcon from "media/icons/orthographic.svg?react";
import SectionIcon from "media/icons/section.svg?react";
import XRayIcon from "media/icons/xray.svg?react";
import { ProcoreToolMode } from "slices/procoreUx";
import { DEFAULT_SECTION_PICKER } from "slices/procoreUx/utils";
import { telemetry } from "telemetry";

import { PROCORE_BOTTOM_ENTRY } from "../constants";
import { BottomToolbarButton } from "./BottomToolbarButton";
import { bottomToolbarActions, selectIsRenderMenuOpen } from "./slice";

interface ToolsButtonRendererProps {
    toolbarId: string;
}

/** Button ids after which a vertical divider should be injected. */
const DIVIDER_AFTER_IDS = new Set<string>(["xray-toggle"]);

/**
 * Custom button renderer for tools content buttons
 */
function ToolsButtonRenderer({ toolbarId }: ToolsButtonRendererProps) {
    const buttons = useToolbarButtons(toolbarId);

    if (buttons.length === 0) {
        return null;
    }

    return (
        <Box display="flex" alignItems="center" gap={`${PROCORE_BOTTOM_ENTRY.GAP}px`}>
            {buttons.flatMap((buttonEntry) => {
                const { config, state } = buttonEntry;

                if (config.separator || !state.visible) {
                    return [];
                }

                const button = (
                    <BottomToolbarButton
                        key={config.id}
                        icon={config.icon}
                        label={config.title || ""}
                        tooltip={config.tooltip?.toolName || config.title || ""}
                        active={state.active}
                        onClick={config.onClick}
                        buttonId={config.id}
                        buttonRef={config.ref}
                    />
                );

                if (DIVIDER_AFTER_IDS.has(config.id)) {
                    return [button, <Divider key={`d-${config.id}`} orientation="vertical" flexItem sx={{ mx: 1 }} />];
                }

                return [button];
            })}
        </Box>
    );
}

/**
 * Content for the Tools tab - activates tools
 */
export function BottomToolbarToolsContent() {
    const toolbarDispatch = useDispatchToolbars();
    const dispatch = useAppDispatch();
    const enterToolMode = useEnterToolMode();
    const toggleXRayMode = useToggleXRayMode();
    const cameraType = useAppSelector(selectCameraType);
    const isXRayActive = useAppSelector(selectIsXRayActive);
    const isRenderMenuOpen = useAppSelector(selectIsRenderMenuOpen);
    const mainObject = useAppSelector(selectMainObject);
    const mainObjectPick = useAppSelector(selectMainObjectPick);
    const renderButtonRef = useRef<HTMLButtonElement>(null);
    const {
        state: { view, scene, dataAccess },
    } = useExplorerGlobals(true);

    const handleCameraToggle = useCallback(() => {
        if (!view || !scene) {
            return;
        }
        void track(Events.toolbar.cameraTapped, { origin: EventOrigin.Toolbar });

        if (cameraType === CameraType.Pinhole) {
            getOrthoFromPinhole(view, { dataAccess, up: scene?.up, mainObject })
                .then(async (orthographicParams) => {
                    dispatch(
                        renderActions.setCamera({
                            type: CameraType.Orthographic,
                            goTo: { ...orthographicParams, flyTime: 0 },
                        }),
                    );
                    // Reveal a deep selected object with a world-fixed clip plane in front of it.
                    const clipPlane = await getOrthoFitClipPlane({ view, dataAccess, mainObject, mainObjectPick });
                    if (clipPlane) {
                        dispatch(renderActions.addClippingPlane(clipPlane));
                    }
                })
                .catch((error) => {
                    console.warn("Failed to build orthographic camera:", error);
                    telemetry.trackCaughtException({
                        source: "BottomToolbarToolsContent.cameraToggle",
                        category: "render",
                        error,
                    });
                    dispatch(renderActions.setCamera({ type: CameraType.Orthographic }));
                });
        } else {
            dispatch(renderActions.setCamera({ type: CameraType.Pinhole }));
            dispatch(renderActions.removeTemporaryClippingPlanes());
        }
    }, [dispatch, view, scene, dataAccess, cameraType, mainObject, mainObjectPick]);

    const buttons: ToolbarButtonEntry[] = useMemo(
        () => [
            {
                config: {
                    id: "camera-toggle",
                    icon: <OrthographicIcon />,
                    onClick: handleCameraToggle,
                    title: "Ortho",
                    tooltip: {
                        toolName: "Orthographic camera",
                        shortcutKey: "",
                    },
                },
                state: {
                    active: cameraType === CameraType.Orthographic,
                    disabled: !view || !scene,
                    // Hidden on mobile (BM-12544), kept wired up for an expected return.
                    visible: false,
                },
            },
            {
                config: {
                    id: "render",
                    icon: <LayersFill />,
                    ref: renderButtonRef,
                    onClick: () => dispatch(bottomToolbarActions.toggleRenderMenu()),
                    title: "Render",
                    tooltip: {
                        toolName: "Render modes",
                        shortcutKey: "",
                    },
                },
                state: {
                    active: isRenderMenuOpen,
                    disabled: false,
                    visible: true,
                },
            },
            {
                config: {
                    id: "xray-toggle",
                    icon: <XRayIcon />,
                    onClick: toggleXRayMode,
                    title: "X-Ray",
                    tooltip: {
                        toolName: "X-Ray mode",
                        shortcutKey: "",
                    },
                },
                state: {
                    active: isXRayActive,
                    disabled: false,
                    visible: true,
                },
            },
            // {
            //     config: {
            //         id: ProcoreToolMode.Markup,
            //         icon: <Brush />,
            //         onClick: () => dispatch(procoreUxActions.setActiveToolMode(ProcoreToolMode.Markup)),
            //         title: "Markup",
            //         tooltip: {
            //             toolName: "Markup tools",
            //             shortcutKey: "",
            //         },
            //     },
            //     state: {
            //         active: false,
            //         disabled: false,
            //         visible: true,
            //     },
            // },
            // {
            //     config: {
            //         id: ProcoreToolMode.Create,
            //         icon: <Add />,
            //         onClick: () => dispatch(procoreUxActions.setActiveToolMode(ProcoreToolMode.Create)),
            //         title: "Create",
            //         tooltip: {
            //             toolName: "Create objects",
            //             shortcutKey: "",
            //         },
            //     },
            //     state: {
            //         active: false,
            //         disabled: false,
            //         visible: true,
            //     },
            // },
            {
                config: {
                    id: ProcoreToolMode.Measure,
                    icon: <Ruler />,
                    onClick: () => {
                        enterToolMode({ mode: ProcoreToolMode.Measure });
                        void track(Events.toolbar.measureTapped, { origin: EventOrigin.Toolbar });
                    },
                    title: "Measure",
                    tooltip: {
                        toolName: "Measurement tools",
                        shortcutKey: "",
                    },
                },
                state: {
                    active: false,
                    disabled: false,
                    visible: true,
                },
            },
            {
                config: {
                    id: ProcoreToolMode.Section,
                    icon: <SectionIcon />,
                    onClick: () => {
                        enterToolMode({ picker: DEFAULT_SECTION_PICKER });
                        void track(Events.toolbar.sectionTapped, { origin: EventOrigin.Toolbar });
                    },
                    title: "Section",
                    tooltip: {
                        toolName: "Section tools",
                        shortcutKey: "",
                    },
                },
                state: {
                    active: false,
                    disabled: false,
                    visible: true,
                },
            },
        ],
        [
            cameraType,
            view,
            scene,
            handleCameraToggle,
            isXRayActive,
            toggleXRayMode,
            isRenderMenuOpen,
            dispatch,
            enterToolMode,
        ],
    );

    const toolbarId = "bottom-toolbar-tools";

    useEffect(() => {
        toolbarDispatch(
            toolbarsActions.addToolbar({
                id: toolbarId,
                buttons,
            }),
        );

        return () => {
            toolbarDispatch(toolbarsActions.removeToolbar(toolbarId));
        };
    }, [toolbarDispatch, buttons]);

    return <ToolsButtonRenderer toolbarId={toolbarId} />;
}
